import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import SupportTicketDetail from 'components/support/SupportTicketDetail';
import { supportTicketService } from 'api/services/SupportTicketService';
import type { SupportTicketViewModel } from 'api/types/support-ticket';
import Button from 'components/Button';

const AdminSupportTicketDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState<SupportTicketViewModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  const fetchTicket = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await supportTicketService.getAdminTicketById(tid);
      if (result.isSuccess && result.data) {
        setTicket(result.data);
      } else {
        setError(result.message ?? 'Talep bulunamadı.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) fetchTicket(id);
    else setError('Talep kodu belirtilmedi.');
  }, [id, fetchTicket]);

  const handleResolve = async () => {
    if (!id || resolving) return;
    setResolving(true);
    try {
      const result = await supportTicketService.resolve(id);
      if (result.isSuccess) {
        navigate('/admin-panel/support-tickets');
        return;
      }
    } finally {
      setResolving(false);
    }
  };

  if (loading && !ticket) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <>
        <div className="alert alert-danger">{error ?? 'Talep bulunamadı.'}</div>
        <Link to="/admin-panel/support-tickets" className="btn btn-primary">
          Listeye Dön
        </Link>
      </>
    );
  }

  const statusLower = String(ticket.supportStatusName ?? '').toLowerCase();
  const isResolved = statusLower.includes('resolved') ||
    statusLower.includes('çözüldü') ||
    statusLower.includes('kapalı') ||
    statusLower.includes('closed');

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap align-items-center pb-2 mb-3 border-bottom">
        <div className="d-flex align-items-center">
          <Link
            to="/admin-panel/support-tickets"
            className="btn btn-link text-muted text-decoration-none me-2"
          >
            <i className="fas fa-arrow-left" />
          </Link>
          <h1 className="h2 mb-0">Talep Detayı</h1>
        </div>
        {!isResolved && ticket.conversationId && (
          <Button
            className="btn btn-success"
            loading={resolving}
            disabled={resolving}
            onClick={() => { void handleResolve(); }}
          >
            Çözüldü İşaretle
          </Button>
        )}
      </div>
      <SupportTicketDetail
        ticket={ticket}
        currentUserId={user?.id ?? ''}
        backPath="/admin-panel/support-tickets"
        backLabel="Listeye dön"
        isResolved={isResolved}
      />
    </>
  );
};

export default AdminSupportTicketDetailPage;
